//-- Copyright (c) 2021 TMT Insights Group, LLC
import { useContext, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import styled from '@emotion/styled';
import { Button, Container, Divider, Grid, MenuItem, Paper, TextField } from '@mui/material';

import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { getWorkflows } from '@/redux/slices/profileSlice';
import { selectWorkflows } from '@/redux/selectors/profileSelectors';
import { selectAudioLanguages, selectJobWorkflows, selectMediaResolutions, selectSubtitlesLanguages } from '@/redux/selectors/constantsSelectors';
import { ConfigContext } from '@/contextAPI/configContext';
import { useToast } from '@/components/_common/Toast';
import { findandFormatUTCDateInString } from '@/_utils/date';
import { PolarisIcon } from '@/exports/columnCommonComponents';
import Fieldset from '@/components/_common/Fieldset';
import CustomerSearch from '@/components/_common/CustomerSearch';
import SectionHeading from '@/components/_common/SectionHeading';
import Autocomplete from '@/components/_common/Form/Autocomplete';
import SchemaForm from '@/components/_common/SchemaForm/SchemaForm';
import DropdownSelect from '@/components/_common/Form/DropdownSelect';
import LanguageSelector from '@/components/_common/LanguageSelector/LanguageSelector';
import ProfileBuilderNotifications from '@/components/ProfileBuilder/ProfileBuilderNotifications';

const ProfileBuilderForm = ({ profile, setEditing, saveProfile }) => {
  const history = useHistory();
  const dispatch = useAppDispatch();
  const mediaResolutions = useAppSelector(selectMediaResolutions);
  const jobWorkflows = useAppSelector(selectJobWorkflows);
  const workflows = useAppSelector(selectWorkflows);
  const AudioLanguages = useAppSelector(selectAudioLanguages);
  const SubtitleLanguages = useAppSelector(selectSubtitlesLanguages);
  const disableCustomerCreateInProfileCreationFlow = useContext(ConfigContext)?.orderPlacementConfig?.disableCustomerCreateInProfileCreationFlow;
  const { success, error } = useToast();
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [fields, setFields] = useState({});
  const isEdit = profile?.id && setEditing;
  const templateSchema = workflows?.[fields.workflow]?.template_schema;
  const schemaRef = useRef();
  const workflow = fields.workflow ? workflows?.[fields.workflow] : undefined;

  useEffect(() => {
    if (isEdit) {
      setFields(profile);
    }
    dispatch(getWorkflows());
  }, []);

  useEffect(() => {
    if (fields.workflow) {
      if (isEdit && fields.workflow === profile?.workflow) {
        setFields({ ...fields, template_inputs: profile.template_inputs });
      } else if (templateSchema) {
        // get default values from template schema
        const template_inputs = {};
        const templateProps = Object.keys(templateSchema?.properties);
        templateProps?.forEach((key) => {
          const defaultValue = templateSchema.properties[key].default;
          if (defaultValue) template_inputs[key] = defaultValue;
        });
        setFields({ ...fields, template_inputs });
      }
    }
  }, [fields.workflow]);

  const updateField = (name, value) => {
    setFields({ ...fields, [name]: value });
    clearError(name);
  };

  const onInputChange = (e) => updateField(e.target.id, e.target.value);
  const onDropdownChange = (value, name) => {
    updateField(name, value);
  };
  const onAutocompleteChange = (e, value, name) => {
    updateField(name, value);
  };
  const clearError = (field) => setErrors({ ...errors, [field]: false });

  const validate = () => {
    const requiredFields = {
      name: 'Profile Name',
      customer_name: 'Customer',
      workflow: 'Workflow'
    };
    const toastErrors = [];
    const errors = {};
    const email_notification = fields.email_notification;

    const isSchemaValid = schemaRef.current?.validateForm();
    if (!isSchemaValid) {
      errors.schema = true;
    }
    Object.keys(requiredFields).forEach((field) => {
      if (!fields[field]?.length) {
        toastErrors.push(`Field '${requiredFields[field]}' is required`);
        errors[field] = true;
      }
    });

    if (email_notification) {
      if (!email_notification.template) {
        delete email_notification.to;
        delete email_notification.cc;
        delete email_notification.bcc;
      } else if (!email_notification.to) {
        toastErrors.push('To send a delivery notification, it needs at least one "To:" email address.');
        errors.email_notification.to = true;
      }
    }
    console.error(errors);
    setErrors(errors);
    return [...toastErrors, ...(errors.schema ? ['Please fix errors in the form schema.'] : [])];
  };

  const onSubmit = (e) => {
    e.preventDefault();
    const { id, name, customer_name, workflow, ...data } = fields;
    const errors = validate();

    if (!errors.length) {
      const payload = {
        name,
        id,
        customer_name,
        data: {
          workflow,
          ...data
        }
      };

      setLoading(true);
      saveProfile(payload)
        .then(() => success(`Profile has been ${isEdit ? 'updated' : 'saved'}!`))
        .catch((err) => {
          console.error('Profile unable to be saved: ', err);
        })
        .then(() => setLoading(false));
    } else {
      errors.map(error);
    }
  };

  const onCancel = () => {
    if (profile?.id && setEditing) {
      setEditing(false);
    } else {
      history.push('/profiles');
    }
  };

  return (
    <StyledContainer loading={loading ? true : undefined}>
      <form onSubmit={onSubmit}>
        <StyledPaper>
          <Grid container spacing={5}>
            <Grid item sm={6} md={3}>
              <CustomerSearch
                value={fields.customer_name || null}
                error={errors.customer_name}
                canAddCustomer={!disableCustomerCreateInProfileCreationFlow}
                onChange={(val) => updateField('customer_name', val)}
              />
            </Grid>
            <Grid item sm={6} md={3}>
              <TextField
                id="name"
                label="Profile Name"
                value={findandFormatUTCDateInString(fields.name) || ''}
                variant="filled"
                fullWidth
                onChange={onInputChange}
                error={errors.name}
              />
            </Grid>
            <Grid item sm={6} md={3}>
              <Autocomplete
                options={Object.values(jobWorkflows)}
                label="Workflow"
                value={fields.workflow}
                error={errors.workflow}
                name="workflow"
                onChange={(e, value) => onAutocompleteChange(e, value, 'workflow')}
              />
            </Grid>
            <Grid item sx={{ ml: 'auto' }}>
              <Grid container>
                <Button variant="outlined" color="default" onClick={onCancel} style={{ marginRight: 10 }}>
                  Cancel
                </Button>
                <Button variant="contained" color="primary" type="submit">
                  Save {isEdit ? 'Changes' : 'Profile'}
                </Button>
              </Grid>
            </Grid>
          </Grid>

          {/* COMPONENTS */}
          <Divider sx={{ mb: 4, mt: 6 }} />

          <Grid container>
            {!workflow || workflow?.workflow_version === 'v2' ? null : (
              <>
                <Grid item xs={12}>
                  <SectionHeading icon={<PolarisIcon icon="components" />}>Components</SectionHeading>
                </Grid>

                <Grid item xs={12} lg={6}>
                  <Grid container columnSpacing={5} sx={{ mb: 3 }}>
                    <Grid item sm={6}>
                      <Fieldset label="Media" color="primary" style={{ height: '100%' }}>
                        <Grid container spacing={2} style={{ marginTop: 0 }}>
                          <Grid item sm={8}>
                            <DropdownSelect
                              label="Resolution"
                              name="media_resolution"
                              fullWidth
                              onChange={onDropdownChange}
                              value={fields.media_resolution}
                            >
                              <MenuItem value={null}>-</MenuItem>
                              {mediaResolutions.map((val) => (
                                <MenuItem key={val.value} value={val.value}>
                                  {val.name}
                                </MenuItem>
                              ))}
                            </DropdownSelect>
                          </Grid>
                        </Grid>
                      </Fieldset>
                    </Grid>
                  </Grid>

                  <Grid container columnSpacing={5} sx={{ mb: 3 }}>
                    <Grid item sm={12}>
                      <Fieldset label="Audio" color="secondary">
                        <Grid container spacing={2} style={{ marginTop: 0 }}>
                          <Grid item sm={12}>
                            <LanguageSelector
                              color="secondary"
                              languageCodes={fields.audio_tracks}
                              onChange={(codes) => updateField('audio_tracks', codes)}
                              languages={AudioLanguages}
                            />
                          </Grid>
                        </Grid>
                      </Fieldset>
                    </Grid>
                  </Grid>

                  <Grid container columnSpacing={5} sx={{ mb: 3 }}>
                    <Grid item sm={12}>
                      <Fieldset label="Subtitle" color="default">
                        <Grid container spacing={2} style={{ marginTop: 0 }}>
                          <Grid item sm={12}>
                            <LanguageSelector
                              color="default"
                              languageCodes={fields.subtitle_tracks}
                              onChange={(codes) => updateField('subtitle_tracks', codes)}
                              languages={SubtitleLanguages}
                            />
                          </Grid>
                        </Grid>
                      </Fieldset>
                    </Grid>
                  </Grid>
                </Grid>
              </>
            )}
            {!!templateSchema && (
              <SchemaForm
                id={fields.workflow}
                formRef={schemaRef}
                schema={templateSchema}
                formData={fields.template_inputs || {}}
                onChange={(data) => updateField('template_inputs', data.formData)}
                tagName="div"
                extraData={{ profile: fields }}
              />
            )}
          </Grid>

          {/* EMAIL NOTIFICATION */}
          <Divider sx={{ mb: 4, mt: 6 }} />
          <SectionHeading icon={<PolarisIcon icon="email" />} style={{ marginTop: 60 }}>
            Email Notification
          </SectionHeading>
          <Grid container spacing={5}>
            <Grid item xs={12}>
              <ProfileBuilderNotifications email_notification={fields.email_notification} errors={errors} updateField={updateField} />
            </Grid>
          </Grid>
        </StyledPaper>

        {/* BUTTONS */}
        <Grid sx={{ mt: 3 }} container justifyContent="flex-end">
          <Button variant="text" color="default" onClick={onCancel} style={{ marginRight: 10 }}>
            Cancel
          </Button>
          <Button variant="contained" color="primary" type="submit">
            Save {isEdit ? 'Changes' : 'Profile'}
          </Button>
        </Grid>
      </form>
    </StyledContainer>
  );
};

export default ProfileBuilderForm;

const StyledContainer = styled(Container)`
  ${(props) =>
    props.loading
      ? `
    opacity: 0.2;
    pointer-events: none;
  `
      : null};
`;

const StyledPaper = styled(Paper)`
  padding: ${(props) => props.theme.spacing(5)};
  & > .MuiGrid-container {
    margin-bottom: ${(props) => props.theme.spacing(5)};
  }
`;
